/* Липкая шапка (APP-SPEC §1.8): блок .bc-sticky; класс sc ставит общий слушатель прокрутки (src/ui/sticky.ts).
   flush — шапка первым блоком прокрутки (верхнее поле прокрутки уходит внутрь шапки). className держится постоянным:
   иначе React при перерисовке снял бы sc, поставленный слушателем. */
import { forwardRef, useLayoutEffect, useRef, type HTMLAttributes, type ReactNode } from 'react';
import { installSticky, stickyRoot, stickySync } from './sticky-core';

interface Props extends HTMLAttributes<HTMLDivElement> {
  flush?: boolean;
  children: ReactNode;
}

/* Сверка класса sc после перерисовки — одним кадром на все шапки, а не в useLayoutEffect: там обход предков (getComputedStyle) и обмер
   шапки заставляли браузер считать стили и раскладку свежесобранной страницы посреди сборки (вход в «Студию»: +31 мс на ПК, на слабом
   телефоне ×5). В кадре раскладка всё равно идёт — чтение в нём ничего не удваивает. Прокручиваемый предок запоминается на шапке. */
const due = new Set<HTMLElement>();
let frame = 0;
function later(h: HTMLElement) {
  due.add(h);
  if (frame) return;
  frame = requestAnimationFrame(() => {
    frame = 0;
    const L = [...due];
    due.clear();
    const roots = new Set<Element>();
    L.forEach(el => {
      if (!el.isConnected) return;
      const c = el as HTMLElement & { _bcSr?: Element };
      if (!c._bcSr || !c._bcSr.isConnected || !c._bcSr.contains(el)) c._bcSr = stickyRoot(el);
      roots.add(c._bcSr);
    });
    roots.forEach(stickySync);
  });
}

export const Sticky = forwardRef<HTMLDivElement, Props>(function Sticky({ flush, className, children, ...rest }, outer) {
  const ref = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    installSticky();
  }, []);
  useLayoutEffect(() => {
    if (ref.current) later(ref.current);
  });
  return (
    <div
      {...rest}
      ref={el => {
        ref.current = el;
        if (typeof outer === 'function') outer(el);
        else if (outer) outer.current = el;
      }}
      className={'bc-sticky' + (flush ? ' flush' : '') + (className ? ' ' + className : '')}
    >
      {children}
    </div>
  );
});
